import React, { useState, useCallback } from "react";
import { QueryClient } from "@tanstack/react-query";
import { BookingV4 } from "../../../../../types/bookings";
import { InvoiceData, InvoiceViewOptions } from "../../../../../types/invoices";
import {
  saveInvoice,
  deleteInvoice,
  sendInvoiceEmail,
  generateInvoiceViaApi,
  getInvoicePrintHtml,
  downloadInvoicePdf,
  downloadInvoiceCsv
} from "../../../../../APIs/Booking/InvoiceOperations";
import { buildInvoiceBody } from "./invoiceUtils";

interface UseInvoiceActionsParams {
  invoice: InvoiceData | null;
  setInvoice: React.Dispatch<React.SetStateAction<InvoiceData | null>>;
  setIsEditing: (editing: boolean) => void;
  bookingId: string;
  booking: BookingV4 | null;
  diaryContext: {
    setDiaryContext: (updates: Record<string, unknown>) => void;
  };
  refetchInvoice: () => Promise<unknown>;
  refetchRevisions: () => Promise<unknown>;
  queryClient: QueryClient;
  setShowDeleteConfirm: (show: boolean) => void;
  handleAddInvoice: () => void;
  handleAddPreorderItems: () => void;
}

interface InvoiceActionsResult {
  isSaving: boolean;
  isDeleting: boolean;
  isGenerating: boolean;
  isPrinting: boolean;
  isDownloading: boolean;
  isDownloadingCsv: boolean;
  isEmailing: boolean;
  saveError: string | null;
  setSaveError: (error: string | null) => void;
  deleteError: string | null;
  generateError: string | null;
  generateSuccess: boolean;
  emailSuccess: boolean;
  actionError: string | null;
  setActionError: (error: string | null) => void;
  handleSaveInvoice: () => Promise<void>;
  handleDeleteInvoice: () => Promise<void>;
  handleGenerateViaApi: () => Promise<void>;
  handlePrint: (options: InvoiceViewOptions) => Promise<void>;
  handleDownload: (options: InvoiceViewOptions) => Promise<void>;
  handleDownloadCsv: (options: InvoiceViewOptions) => Promise<void>;
  handleEmail: () => Promise<void>;
}

const useInvoiceActions = ({
  invoice,
  setInvoice,
  setIsEditing,
  bookingId,
  booking,
  diaryContext,
  refetchInvoice,
  refetchRevisions,
  queryClient,
  setShowDeleteConfirm,
  handleAddInvoice,
  handleAddPreorderItems
}: UseInvoiceActionsParams): InvoiceActionsResult => {
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generateSuccess, setGenerateSuccess] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleSaveInvoice = useCallback(async (): Promise<void> => {
    if (!invoice || !bookingId) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const invoiceBody = buildInvoiceBody(invoice);
      const result = await saveInvoice(bookingId, invoiceBody);

      if (result) {
        setInvoice(result.invoice);
        setIsEditing(false);

        // Update booking context with tax config and last_updated from response
        // to prevent stale booking errors (matches collins-ui saveInvoice)
        if (booking) {
          const bookingUpdates: Record<string, unknown> = {};
          if (result.invoice_tax_config) {
            bookingUpdates.invoice_tax_config = result.invoice_tax_config;
          }
          if (result.booking_last_updated) {
            bookingUpdates.last_updated = new Date(result.booking_last_updated);
          }
          if (Object.keys(bookingUpdates).length > 0) {
            diaryContext.setDiaryContext({
              selectedBookingV4: {
                ...booking,
                ...bookingUpdates
              }
            });
          }
        }

        await Promise.all([refetchInvoice(), refetchRevisions()]);
        queryClient.invalidateQueries({ queryKey: ["getSelectedBooking"] });
      } else {
        setSaveError("Failed to save invoice. Please try again.");
      }
    } catch {
      setSaveError("An error occurred while saving the invoice.");
    } finally {
      setIsSaving(false);
    }
  }, [
    invoice,
    bookingId,
    booking,
    diaryContext,
    refetchInvoice,
    refetchRevisions,
    queryClient,
    setInvoice,
    setIsEditing
  ]);

  const handleDeleteInvoice = useCallback(async (): Promise<void> => {
    if (!invoice?.id || !bookingId) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const success = await deleteInvoice(bookingId, invoice.id);

      if (success) {
        setInvoice(null);
        setIsEditing(false);
        setShowDeleteConfirm(false);
        await Promise.all([refetchInvoice(), refetchRevisions()]);
        queryClient.invalidateQueries({ queryKey: ["getSelectedBooking"] });
      } else {
        setDeleteError("Failed to delete invoice. Please try again.");
      }
    } catch {
      setDeleteError("An error occurred while deleting the invoice.");
    } finally {
      setIsDeleting(false);
    }
  }, [
    invoice,
    bookingId,
    refetchInvoice,
    refetchRevisions,
    queryClient,
    setInvoice,
    setIsEditing,
    setShowDeleteConfirm
  ]);

  const handleGenerateViaApi = useCallback(async (): Promise<void> => {
    if (!bookingId || isGenerating) return;

    setIsGenerating(true);
    setGenerateError(null);
    setGenerateSuccess(false);
    // Ensure isEditing is false so the useEffect can update
    // the invoice state when refetchInvoice returns fresh data
    setIsEditing(false);

    try {
      const result = await generateInvoiceViaApi(bookingId);

      if (result.booking_last_updated && booking) {
        diaryContext.setDiaryContext({
          selectedBookingV4: {
            ...booking,
            last_updated: new Date(result.booking_last_updated)
          }
        });
      }

      await Promise.all([refetchInvoice(), refetchRevisions()]);
      queryClient.invalidateQueries({ queryKey: ["getSelectedBooking"] });

      setGenerateSuccess(true);
      setIsGenerating(false);

      setTimeout(() => setGenerateSuccess(false), 10000);
    } catch (error) {
      console.error("Failed to generate invoice via API", error);
      setGenerateError(
        "Auto-generation failed. Invoice has been created from preorder data. Please review the items and save."
      );
      setIsGenerating(false);
      setTimeout(() => setGenerateError(null), 10000);

      // Fallback: create a local invoice and populate with preorder items
      // so admins can review/edit rather than adding everything manually
      // (matches collins-ui lines 1874-1882 -- deposits are NOT added on failure)
      handleAddInvoice();
      handleAddPreorderItems();
    }
  }, [
    bookingId,
    isGenerating,
    booking,
    diaryContext,
    refetchInvoice,
    refetchRevisions,
    queryClient,
    handleAddInvoice,
    handleAddPreorderItems,
    setIsEditing
  ]);

  const handlePrint = useCallback(
    async (options: InvoiceViewOptions): Promise<void> => {
      if (!invoice?.id || !bookingId) return;

      setIsPrinting(true);
      setActionError(null);
      try {
        const html = await getInvoicePrintHtml(bookingId, invoice.id, options);

        if (html) {
          const blob = new Blob([html], { type: "text/html" });
          const blobUrl = URL.createObjectURL(blob);
          const printWindow = window.open(blobUrl, "_blank");
          if (printWindow) {
            printWindow.addEventListener("load", () => {
              printWindow.print();
              URL.revokeObjectURL(blobUrl);
            });
          } else {
            URL.revokeObjectURL(blobUrl);
          }
        }
      } catch {
        setActionError("Failed to print invoice. Please try again.");
      } finally {
        setIsPrinting(false);
      }
    },
    [invoice, bookingId]
  );

  const handleDownload = useCallback(
    async (options: InvoiceViewOptions): Promise<void> => {
      if (!invoice?.id || !bookingId) return;

      setIsDownloading(true);
      setActionError(null);
      try {
        await downloadInvoicePdf(bookingId, invoice.id, options);
      } catch {
        setActionError("Failed to download invoice PDF. Please try again.");
      } finally {
        setIsDownloading(false);
      }
    },
    [invoice, bookingId]
  );

  const handleDownloadCsv = useCallback(
    async (options: InvoiceViewOptions): Promise<void> => {
      if (!invoice?.id || !bookingId) return;

      setIsDownloadingCsv(true);
      setActionError(null);
      try {
        await downloadInvoiceCsv(bookingId, invoice.id, options);
      } catch {
        setActionError("Failed to download invoice CSV. Please try again.");
      } finally {
        setIsDownloadingCsv(false);
      }
    },
    [invoice, bookingId]
  );

  const handleEmail = useCallback(async (): Promise<void> => {
    if (!invoice?.id || !bookingId) return;

    setIsEmailing(true);
    setEmailSuccess(false);
    setActionError(null);

    try {
      const success = await sendInvoiceEmail(bookingId, invoice.id);
      if (success) {
        setEmailSuccess(true);
        setTimeout(() => setEmailSuccess(false), 5000);
      }
    } catch {
      setActionError("Failed to send invoice email. Please try again.");
    } finally {
      setIsEmailing(false);
    }
  }, [invoice, bookingId]);

  return {
    isSaving,
    isDeleting,
    isGenerating,
    isPrinting,
    isDownloading,
    isDownloadingCsv,
    isEmailing,
    saveError,
    setSaveError,
    deleteError,
    generateError,
    generateSuccess,
    emailSuccess,
    actionError,
    setActionError,
    handleSaveInvoice,
    handleDeleteInvoice,
    handleGenerateViaApi,
    handlePrint,
    handleDownload,
    handleDownloadCsv,
    handleEmail
  };
};

export default useInvoiceActions;
